import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import {
  DefaultModulesResponse,
  PublicExperience,
  PublicExperienceResponse,
  PublicExperiencesResponse,
} from './public-experience.model';
import { GenericApiResponse, PaginationParams } from '../general-store/general.model';
import { catchError, delay, map, Observable, throwError } from 'rxjs';
import { BulkUpdatePublicExperiencePayload } from './public-experience.actions';

@Injectable({
  providedIn: 'root',
})
export class PublicExperienceService {
  private readonly apiUrl = environment.apiUrl;

  private readonly http = inject(HttpClient);

  public createPublicExperience = (
    payload: PublicExperience,
  ): Observable<PublicExperienceResponse> => {
    return this.http
      .post<PublicExperienceResponse>(`${this.apiUrl}/api/product-design-templates`, payload)
      .pipe(
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error creating recall: ' + error.message));
        }),
      );
  };

  public deletePublicExperience = (id: number): Observable<GenericApiResponse> => {
    return this.http
      .delete<GenericApiResponse>(`${this.apiUrl}/api/product-design-templates/${id}`)
      .pipe(
        map((response) => response),
        catchError((error) => {
          return throwError(
            () => new Error('Error deleting product design templates: ' + error.message),
          );
        }),
      );
  };

  public getPublicExperiences = (
    paginationParams: PaginationParams,
  ): Observable<PublicExperiencesResponse> => {
    return this.http
      .get<PublicExperiencesResponse>(`${this.apiUrl}/api/product-design-templates`, {
        params: {
          page: paginationParams.first / paginationParams.rows + 1,
          limit: paginationParams.rows,
          sortBy: paginationParams.sortBy,
          sortOrder: paginationParams.sortOrder,
          search: paginationParams.search,
          filters: paginationParams.filters,
        },
      })
      .pipe(
        delay(1000),
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error fetching public experiences: ' + error.message));
        }),
      );
  };

  public updatePublicExperience(
    payload: PublicExperience,
    id: number,
  ): Observable<PublicExperienceResponse> {
    return this.http
      .put<PublicExperienceResponse>(`${this.apiUrl}/api/product-design-templates/${id}`, payload)
      .pipe(
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error updating public experience: ' + error.message));
        }),
      );
  }

  public duplicatePublicExperience(id: number): Observable<PublicExperienceResponse> {
    return this.http
      .post<PublicExperienceResponse>(`${this.apiUrl}/api/page-templates/duplicate`, {
        templateId: id,
      })
      .pipe(
        map((response) => response),
        catchError((error) => {
          return throwError(
            () => new Error('Error duplicating public experience: ' + error.message),
          );
        }),
      );
  }

  public getPublicExperience = (id: number): Observable<PublicExperienceResponse> => {
    return this.http
      .get<PublicExperienceResponse>(`${this.apiUrl}/api/product-design-templates/${id}`)
      .pipe(
        map((response) => response),
        catchError((error) => {
          return throwError(() => new Error('Error fetching public experience: ' + error.message));
        }),
      );
  };

  public getAllDefaultModules = (): Observable<DefaultModulesResponse> => {
    return this.http.get<DefaultModulesResponse>(`${this.apiUrl}/api/default-templates`).pipe(
      map((response) => response),
      catchError((error) => {
        return throwError(() => new Error('Error fetching default modules: ' + error.message));
      }),
    );
  };

  public bulkUpdatePublicExperience = (
    payload: BulkUpdatePublicExperiencePayload,
  ): Observable<GenericApiResponse> => {
    return this.http
      .post<GenericApiResponse>(
        `${this.apiUrl}/api/page-templates/bulk-update-page-content`,
        payload,
      )
      .pipe(
        map((response) => response),
        catchError((error) => {
          return throwError(
            () => new Error('Error bulk updating public experience: ' + error.message),
          );
        }),
      );
  };
}
